/**
 * Font helpers for the two chart components.
 *
 * Everything else in this app styles text with CSS, where `rem` works and `font-family` can
 * simply be inherited (docs/MIGRATION-WIDGETS.md §2). A `<canvas>` can do neither: Chart.js
 * wants font sizes in **pixels**, and `ctx.font` needs a concrete family name because there
 * is nothing to inherit from. These two functions are what the legacy `remToPixels()` and
 * `getDefaultFontFamily()` were used for inside the charts, and nowhere else here.
 */

/**
 * Ported verbatim from legacy `utils/functions.ts`.
 *
 * Deliberately reads the root font size on every call rather than caching it: the root size
 * is viewport-dependent (`global.css` steps it 16 → 19 → 20 → 22px), and these values are
 * baked into a Chart.js options object at chart-construction time.
 */
export function remToPixels(remSize: number): number {
  return remSize * parseFloat(getComputedStyle(document.documentElement).fontSize);
}

let cachedFontFamily: string | undefined;

/**
 * The body's computed `font-family`, for the places a canvas needs a literal family name.
 *
 * Legacy `getDefaultFontFamily()` called `getComputedStyle(document.body)` at every call
 * site — sixteen of them, most of which then wrote the value straight back as an inline
 * style, which is what MIGRATION-WIDGETS §2 says not to port. The chart call sites are the
 * exception, so the function survives here in memoised form.
 *
 * Memoising is safe because the value comes from the `* { font-family: HubhubSans-Medium }`
 * rule in `global.css`, which is static: it does not depend on viewport, on `layout.class`,
 * or on whether the font file has finished loading (the computed *family* is the declared
 * name either way). If a future theme ever varies the body font at runtime, this cache is
 * the thing to remove.
 */
export function defaultChartFontFamily(): string {
  cachedFontFamily ??= window.getComputedStyle(document.body, null).getPropertyValue('font-family');
  return cachedFontFamily;
}

/** Test-only: drops the memoised family so a suite can vary the body font. */
export function resetChartFontFamily(): void {
  cachedFontFamily = undefined;
}
